/**
 * Props and value shapes for the FieldIQ handoff components.
 *
 * Imported from jaywyncapital/-fieldiq-frontend-manus, root src/ only. These
 * components are PRESENTATIONAL: every one of them takes already-formatted
 * strings and renders them. They fetch nothing, compute nothing, and format no
 * number themselves — the host decides what a total or a date says, which keeps
 * this file out of the "two answers to one question" trap in CLAUDE.md.
 *
 * `displayValue` below is this repo's em-dash rule expressed as a function, and
 * every optional field in every ledger goes through it. Note it rejects the
 * empty string as well as null and undefined, so a blank API value renders as a
 * gap rather than as nothing at all. It deliberately does NOT touch 0 — a real
 * zero is data and prints as "0".
 */
import type { ReactNode } from "react";

export type LedgerTone = "neutral" | "info" | "success" | "warning" | "danger" | "current";

export type OptionalField = string | number | null | undefined;

/** Render host-absent values consistently; never imply a missing value is zero. */
export function displayValue(value: OptionalField): string | number {
  return value === null || value === undefined || value === "" ? "—" : value;
}

export interface LedgerStatus {
  label: string;
  tone: LedgerTone;
}

export interface HeaderAction {
  label: string;
  emphasis?: "primary" | "secondary";
  onPress?: () => void;
}

export interface FieldIQPageFrameProps {
  brandLabel: string;
  brandSubline?: string;
  backLabel?: string;
  onBack?: () => void;
  title: string;
  description?: string;
  actions?: HeaderAction[];
  children: ReactNode;
}

export interface TimeAwayRequest {
  id: string;
  employeeName: string;
  role: string | null;
  dateRange: string | null;
  leaveType: string | null;
  reason: string | null;
  coverage: string | null;
  status: LedgerStatus;
  decision: string | null;
}

export interface TimeAwayLedgerProps {
  requests: TimeAwayRequest[];
  selectedRequestId?: string;
  onRequestSelect?: (requestId: string) => void;
  onApproveRequest?: (requestId: string) => void;
  onRejectRequest?: (requestId: string) => void;
  emptyMessage?: string;
}

export interface RateBook {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
  clientScope: string | null;
  version: string | null;
  effectiveDate: string | null;
  lineCount: number | null;
  status: LedgerStatus;
}

export interface RateBookLedgerProps {
  books: RateBook[];
  selectedBookId?: string;
  onBookSelect?: (bookId: string) => void;
  onCreateBook?: () => void;
  emptyMessage?: string;
}

export interface CommercialOffer {
  id: string;
  customer: string;
  priceSource: string | null;
  validity: string | null;
  total: string | null;
  status: LedgerStatus;
  conversion: string | null;
}

export interface OfferLedgerProps {
  offers: CommercialOffer[];
  selectedOfferId?: string;
  onOfferSelect?: (offerId: string) => void;
  onCreateOffer?: () => void;
  emptyMessage?: string;
}

export interface ReceiptEvidence {
  id: string;
  employeeName: string;
  category: string | null;
  merchant: string | null;
  submittedAt: string | null;
  evidenceFile: string | null;
  allocation: string | null;
  paymentSource: string | null;
  total: string | null;
  status: LedgerStatus;
  closeout: string | null;
}

export interface ReceiptLedgerProps {
  receipts: ReceiptEvidence[];
  selectedReceiptId?: string;
  onReceiptSelect?: (receiptId: string) => void;
  onSubmitReceipt?: () => void;
  emptyMessage?: string;
}

export interface InsightDefinition {
  id: string;
  family: string;
  title: string;
  description: string | null;
  sourceLabels: string[];
  scopeLabel: string | null;
}

export interface InsightCatalogProps {
  insights: InsightDefinition[];
  onRunInsight?: (insightId: string) => void;
  emptyMessage?: string;
}
